import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Camera } from 'lucide-react'
import {
  createProfile,
  getProfile,
  reportOnboardingStep,
  updateProfile,
  type ProfilePayload,
} from '../lib/api'
import { useUserStore } from '../store/userStore'
import { useTelegramBackButton } from '../lib/telegram'
import BirthdateField from '../components/BirthdateField'

// Consolidated from 5 screens to 2 (see docs/ui-references/ton-dating
// onboarding overhaul): "identity" bundles name/bio/birthdate/gender onto
// one page, "preference" is seeking alone. domain.OnboardingSteps
// (internal/domain/onboarding.go) and the /stats abandonment breakdown were
// migrated to match — see migration 000021.
type Step = 'identity' | 'preference'

const STEPS: Step[] = ['identity', 'preference']

const STEP_LABEL_KEYS: Record<Step, string> = {
  identity: 'onboarding.stepIdentity',
  preference: 'onboarding.stepPreference',
}

interface FormData {
  display_name: string
  birthdate: string
  gender: '' | 'male' | 'female'
  seeking: '' | 'male' | 'female' | 'both'
  bio: string
}

const INITIAL: FormData = {
  display_name: '',
  birthdate: '',
  gender: '',
  seeking: '',
  bio: '',
}

const stepVariants = {
  enter: (dir: number) => ({ x: dir * 32, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.22, ease: 'easeOut' } },
  exit: (dir: number) => ({
    x: dir * -32,
    opacity: 0,
    transition: { duration: 0.15, ease: 'easeIn' },
  }),
}

export default function OnboardingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isEditMode = searchParams.get('edit') === 'true'
  const setProfileComplete = useUserStore((s) => s.setProfileComplete)

  const [step, setStep] = useState<Step>('identity')
  const [direction, setDirection] = useState<1 | -1>(1)
  const [form, setForm] = useState<FormData>(INITIAL)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isEditMode) return
    getProfile().then((p) => {
      if (!p) return
      setForm({
        display_name: p.display_name ?? '',
        birthdate: p.birthdate ?? '',
        gender: (p.gender as FormData['gender']) ?? '',
        seeking: (p.seeking as FormData['seeking']) ?? '',
        bio: p.bio ?? '',
      })
    })
  }, [isEditMode])

  // Hardware back: edit mode always returns to /swipe. For first-time
  // onboarding, the identity page's previous screen is the welcome/consent
  // gate (WelcomePage); from the preference page, go back to the identity
  // page — mirroring the on-screen "Back" button below.
  useTelegramBackButton(
    isEditMode
      ? () => navigate('/swipe')
      : step === 'identity'
        ? () => navigate('/welcome')
        : () => {
            setDirection(-1)
            setStep('identity')
          },
  )

  const stepIndex = STEPS.indexOf(step)
  const progress = ((stepIndex + 1) / STEPS.length) * 100

  // Report each step transition to the server for the admin /stats
  // abandonment breakdown. Skipped in edit mode — editors aren't part of
  // the onboarding funnel. Best-effort: a failed report never blocks the UX.
  useEffect(() => {
    if (isEditMode) return
    reportOnboardingStep(step).catch(() => {})
  }, [step, isEditMode])

  function isCurrentStepValid(): boolean {
    switch (step) {
      case 'identity':
        return (
          form.display_name.trim().length >= 1 &&
          form.display_name.trim().length <= 50 &&
          /^\d{4}-\d{2}-\d{2}$/.test(form.birthdate) &&
          form.gender !== ''
        )
      case 'preference':
        return form.seeking !== ''
    }
  }

  async function handleNext() {
    if (!isCurrentStepValid()) return
    if (stepIndex < STEPS.length - 1) {
      setDirection(1)
      setStep(STEPS[stepIndex + 1])
      return
    }
    // Final step — submit
    await handleSubmit()
  }

  // Accepts an explicit `current` snapshot rather than always reading the
  // `form` state variable — the preference page auto-submits from inside a
  // setTimeout (see below) fired the same tick as its setForm call, so the
  // closure at that point still has the *previous* render's `form` (missing
  // the seeking value just selected). Passing the up-to-date object
  // straight from the click handler sidesteps that stale-closure race
  // instead of waiting on a re-render that hasn't happened yet.
  async function handleSubmit(current: FormData = form) {
    // Guards against a double POST: the preference page auto-submits 150ms
    // after tapping a seeking option (see below), so a manual "Finish" tap
    // inside that window would otherwise race a second handleSubmit call.
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const payload: ProfilePayload = {
        display_name: current.display_name.trim(),
        birthdate: current.birthdate,
        gender: current.gender as ProfilePayload['gender'],
        seeking: current.seeking as ProfilePayload['seeking'],
        ...(current.bio.trim() && { bio: current.bio.trim() }),
      }
      if (isEditMode) {
        await updateProfile(payload)
        setProfileComplete(true)
        navigate('/swipe', { replace: true })
      } else {
        // Try create first; if profile already exists, fall back to update.
        try {
          await createProfile(payload)
        } catch {
          await updateProfile(payload)
        }
        setProfileComplete(true)
        navigate('/photos', { replace: true })
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('onboarding.genericError'))
      setSubmitting(false)
    }
  }

  const today = new Date()
  const maxBirthdate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
  const minBirthdate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate())

  return (
    <div className="flex min-h-dvh flex-col bg-tg-bg px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        {isEditMode && (
          <div className="mb-4">
            <div className="mb-3 flex items-center gap-3">
              <button
                onClick={() => navigate('/swipe')}
                className="text-sm font-medium text-tg-hint"
              >
                ← {t('onboarding.back')}
              </button>
              <span className="text-base font-semibold text-tg-text">
                {t('onboarding.editProfile')}
              </span>
            </div>
            <button
              onClick={() => navigate('/photos')}
              className="flex w-full items-center gap-2 rounded-xl bg-tg-secondary-bg px-4 py-3 text-left text-sm font-medium text-tg-text"
            >
              <Camera className="h-4 w-4" />
              {t('onboarding.managePhotos')}
            </button>
          </div>
        )}
        <div className="h-1 w-full rounded-full bg-tg-secondary-bg">
          <div
            className="h-1 rounded-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-tg-hint">
          {t('onboarding.stepProgress', { n: stepIndex + 1, total: STEPS.length })}
        </p>
      </div>

      {/* Animated step label + content */}
      <div className="flex-1 overflow-hidden">
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={step}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="flex h-full flex-col"
          >
            <h1 className="mb-6 text-2xl font-semibold text-tg-text">{t(STEP_LABEL_KEYS[step])}</h1>

            <div className="flex-1">
              {step === 'identity' && (
                <div className="flex flex-col gap-4">
                  <input
                    type="text"
                    value={form.display_name}
                    onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                    placeholder={t('onboarding.namePlaceholder')}
                    maxLength={50}
                    autoFocus
                    className="w-full rounded-xl border border-tg-hint bg-tg-secondary-bg px-4 py-3 text-tg-text placeholder-tg-hint focus:outline-none focus:ring-2 focus:ring-primary"
                  />

                  <BirthdateField
                    value={form.birthdate}
                    onChange={(d) => setForm((f) => ({ ...f, birthdate: d }))}
                    minDate={minBirthdate}
                    maxDate={maxBirthdate}
                  />

                  {/* Compact segmented control — gender is one field among
                      several here, so (unlike the old standalone gender
                      screen) selecting it no longer auto-advances; the
                      shared Next button below gates the whole page. */}
                  <div className="flex gap-2">
                    {(
                      [
                        { value: 'male', labelKey: 'onboarding.genderMale' },
                        { value: 'female', labelKey: 'onboarding.genderFemale' },
                      ] as const
                    ).map(({ value: g, labelKey }) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, gender: g }))}
                        className={`flex-1 rounded-xl px-2 py-3 text-center text-sm font-medium capitalize transition-colors ${
                          form.gender === g
                            ? 'bg-primary text-white'
                            : 'bg-tg-secondary-bg text-tg-text'
                        }`}
                      >
                        {t(labelKey)}
                      </button>
                    ))}
                  </div>

                  <div>
                    <textarea
                      value={form.bio}
                      onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                      placeholder={t('onboarding.bioPlaceholder')}
                      maxLength={300}
                      rows={3}
                      className="w-full resize-none rounded-xl border border-tg-hint bg-tg-secondary-bg px-4 py-3 text-tg-text placeholder-tg-hint focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <div className="mt-1 flex justify-between text-xs text-tg-hint">
                      <span>{t('onboarding.bioOptional')}</span>
                      <span>{form.bio.length}/300</span>
                    </div>
                  </div>
                </div>
              )}

              {step === 'preference' && (
                <div className="flex flex-col gap-3">
                  {(
                    [
                      { value: 'male', labelKey: 'onboarding.seekingMen' },
                      { value: 'female', labelKey: 'onboarding.seekingWomen' },
                      { value: 'both', labelKey: 'onboarding.seekingBoth' },
                    ] as const
                  ).map(({ value, labelKey }) => (
                    <button
                      key={value}
                      onClick={() => {
                        const updated = { ...form, seeking: value }
                        setForm(updated)
                        // Last step — auto-advance means auto-submit. Pass
                        // `updated` explicitly; see handleSubmit's doc comment
                        // for why reading the `form` closure here would race.
                        setTimeout(() => {
                          void handleSubmit(updated)
                        }, 150)
                      }}
                      className={`rounded-xl px-4 py-4 text-left font-medium transition-colors ${
                        form.seeking === value
                          ? 'bg-primary text-white'
                          : 'bg-tg-secondary-bg text-tg-text'
                      }`}
                    >
                      {t(labelKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {error && <p className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

      {/* Navigation */}
      <div className="mt-6 flex gap-3">
        {stepIndex > 0 && (
          <button
            onClick={() => {
              setDirection(-1)
              setStep(STEPS[stepIndex - 1])
            }}
            disabled={submitting}
            className="flex-1 rounded-xl bg-tg-secondary-bg py-4 font-medium text-tg-text"
          >
            {t('onboarding.back')}
          </button>
        )}
        <button
          onClick={handleNext}
          disabled={!isCurrentStepValid() || submitting}
          className="flex-1 rounded-xl bg-primary py-4 font-medium text-white disabled:opacity-40"
        >
          {submitting
            ? t('onboarding.saving')
            : stepIndex === STEPS.length - 1
              ? t('onboarding.finish')
              : t('onboarding.next')}
        </button>
      </div>
    </div>
  )
}
